import { motion } from "framer-motion";

const SECTIONS = [
    {
        title: "1. Who We Are",
        body: "Dichotomy Sisters is operated from the United States of America. This Privacy Policy explains what we collect when you visit dichotomysisters.com, begin a recognition, chat with Mara, or purchase readings and ritual objects — and what we never do with it.",
    },
    {
        title: "2. What We Collect",
        body: "Only what a reading requires: your first name, email address, country and time zone; whatever you choose to tell Mara in forms and chat (including one optional photograph and one optional voice note per conversation); birth details if you request a ritual booking; and purchase records. Payment card details are handled entirely by Stripe — we never see, store or touch your card number.",
    },
    {
        title: "3. How It Is Used",
        body: "Your information is used solely to provide your readings: to let Mara answer you, to keep your conversation available when you return, to email you when Mara responds while you are away, and to fulfil and ship ritual orders. We do not sell, rent or trade your information. We do not use your stories for advertising.",
    },
    {
        title: "4. Who Sees Your Words",
        body: "Your conversations are private to you and Mara. They are stored in a secured, access-controlled database and are readable only through Mara's private operator console. Service providers who process payments (Stripe) and deliver email act only on our instructions and under their own privacy terms.",
    },
    {
        title: "5. Retention & Your Rights",
        body: "We keep your conversation so you can return to it. You may ask at any time — inside the Back Room or by replying to any whisper email — to view, correct or permanently delete your information, and we will honor it within 30 days, except where payment or tax law requires us to retain transaction records.",
    },
    {
        title: "6. Cookies & Analytics",
        body: "We use minimal analytics to understand how visitors move through the site. No advertising trackers, no cross-site profiling, no sale of behavioral data.",
    },
    {
        title: "7. Age & Jurisdiction",
        body: "This service is for adults 18 and over. We do not knowingly collect information from minors. Your information is processed in the United States under United States law; by using the site you consent to that processing wherever you are located.",
    },
    {
        title: "8. Contact",
        body: "Privacy questions belong to the same place everything else does: speak to Mara inside the Back Room, or reply to any email you receive from us.",
    },
];

export const PrivacyPage = () => {
    return (
        <div data-testid="privacy-page" className="relative mx-auto min-h-screen max-w-3xl px-5 py-24 sm:px-8">
            <div className="nebula-blob left-[-10%] top-[10%] h-96 w-96 bg-[#5a189a]/20" />
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <a href="/" data-testid="privacy-home-link" className="flex items-center gap-3">
                    <img src="/assets/sisters-mark.png" alt="" className="h-10 w-10 rounded-full ring-1 ring-[#c77dff]/40" />
                    <span className="font-cinzel text-sm font-semibold tracking-[0.3em] text-[#f8f7ff]">
                        DICHOTOMY <span className="text-[#c77dff]">SISTERS</span>
                    </span>
                </a>

                <p className="eyebrow mb-4 mt-14">The Quiet Print · United States of America</p>
                <h1 className="text-glow-lilac font-display text-3xl font-bold tracking-wide text-[#f8f7ff] sm:text-4xl">
                    PRIVACY POLICY
                </h1>
                <p className="mt-4 font-cormorant text-base italic text-[#e0aaff]/70">
                    Effective September 7, 2026 · What is whispered to the mirror stays with the mirror
                </p>

                <div data-testid="privacy-us-notice" className="glass-card mt-8 p-6">
                    <h2 className="font-cinzel text-sm font-semibold tracking-[0.15em] text-[#e0aaff]">
                        DATA PROTECTION &amp; US JURISDICTION NOTICE
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-[#f8f7ff]/75">
                        Dichotomy Sisters LLC operates strictly under United States data privacy frameworks. By
                        submitting your personal details, intake narratives, or situational contexts to Mara Vey, you
                        acknowledge that your data is processed on secure US cloud servers. We comply with the US
                        Federal Trade Commission (FTC) guidelines regarding consumer data protection and maintain an
                        absolute vault of client confidentiality. We do not disclose client narratives to any external
                        third parties under any circumstances.
                    </p>
                </div>

                <div className="mt-12 space-y-10">
                    {SECTIONS.map((s) => (
                        <section key={s.title} data-testid={`privacy-${s.title.split(".")[0].replace(/\s/g, "-")}`}>
                            <h2 className="font-cinzel text-base font-semibold tracking-[0.15em] text-[#e0aaff]">
                                {s.title}
                            </h2>
                            <p className="mt-3 text-sm leading-relaxed text-[#f8f7ff]/75">{s.body}</p>
                        </section>
                    ))}
                </div>

                <p className="mt-16 border-t border-purple-500/15 pt-8 text-center font-cormorant text-sm italic text-[#c77dff]/60">
                    Crafted with intention in the United States of America. Not every mirror holds a soul.
                </p>
            </motion.div>
        </div>
    );
};
